import "@/index.css";
import "@/components/hub/HubPageChrome.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { LandingSettingsProvider } from "@/context/LandingSettingsContext";
import { ProtectedRoute } from "@/components/admin/ProtectedRoute";
import { ScrollToTop } from "@/components/ScrollToTop";
// Landing / /hub are the only routes eagerly imported so their first
// paint stays as fast as before. Everything else is code-split by
// route and paid for only when the user actually navigates there.
// See the audit note at the top of /app/memory/PRD.md for the
// baseline numbers this refactor targets.
import ProphetsHome from "@/pages/ProphetsHome";
import HubPage from "@/pages/HubPage";
import HubHomePage from "@/pages/HubHomePage";
import { HUB_FEATURE_FLAGS } from "@/lib/hubFeatureFlags";
import PersistentSolRadio from "@/components/hub/PersistentSolRadio";

// ── Lazy routes ─────────────────────────────────────────────────────
// Every non-home page below is loaded on-demand. Their JS is fetched
// only when the corresponding route matches, and never contributes to
// the /hub initial parse cost.
const AdminLogin              = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard          = lazy(() => import("@/pages/AdminDashboard"));
const TheOracle               = lazy(() => import("@/pages/TheOracle"));
const OracleAdmin             = lazy(() => import("@/pages/OracleAdmin"));
const AdminLayout             = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminHome               = lazy(() => import("@/pages/admin/AdminHome"));
const LandingPageAdmin        = lazy(() => import("@/pages/admin/LandingPageAdmin"));
const AdminRaffle             = lazy(() => import("@/pages/admin/AdminRaffle"));
const RafflePage              = lazy(() => import("@/pages/RafflePage"));
const WhitelistPage           = lazy(() => import("@/pages/WhitelistPage"));
const WhitelistCampaignPage   = lazy(() => import("@/pages/WhitelistCampaignPage"));
const EligibilityPage         = lazy(() => import("@/pages/EligibilityPage"));
const AdminWhitelist          = lazy(() => import("@/pages/admin/AdminWhitelist"));
const AdminWhitelistCampaign  = lazy(() => import("@/pages/admin/AdminWhitelistCampaign"));
const AdminGlobalRewards      = lazy(() => import("@/pages/admin/AdminGlobalRewards"));
const HubDashboardPage        = lazy(() => import("@/pages/HubDashboardPage"));
const HubTransmissionsPage    = lazy(() => import("@/pages/HubTransmissionsPage"));
const HubFragmentsPage        = lazy(() => import("@/pages/HubFragmentsPage"));
const HubEarnLandingPage      = lazy(() => import("@/pages/HubEarnLandingPage"));
const HubProfilePage          = lazy(() => import("@/pages/HubProfilePage"));
const HubInvitePage           = lazy(() => import("@/pages/HubInvitePage"));
const HubPortfolioPage        = lazy(() => import("@/pages/HubPortfolioPage"));
const HubQuizPage             = lazy(() => import("@/pages/HubQuizPage"));
const HubDailySpinPage        = lazy(() => import("@/pages/HubDailySpinPage"));
const HubProphecyPage         = lazy(() => import("@/pages/HubProphecyPage"));
const HubMarketplacePage      = lazy(() => import("@/pages/HubMarketplacePage"));
const HubMyPredictionsPage    = lazy(() => import("@/pages/HubMyPredictionsPage"));
const HubPredictorProfilePage = lazy(() => import("@/pages/HubPredictorProfilePage"));
const HubLeaderboardPage      = lazy(() => import("@/pages/HubLeaderboardPage"));
const HubGamesPage            = lazy(() => import("@/pages/HubGamesPage"));
const HubFamilyRacePage       = lazy(() => import("@/pages/HubFamilyRacePage"));
const HubZodiacPage           = lazy(() => import("@/pages/HubZodiacPage"));
const HubGoldenVaultPage      = lazy(() => import("@/pages/HubGoldenVaultPage"));
const HubHeadquartersPage     = lazy(() => import("@/pages/HubHeadquartersPage"));
const HubHeadquartersLandingPage = lazy(() => import("@/pages/HubHeadquartersHelixPage"));
const HubHoldAndEarnPage      = lazy(() => import("@/pages/HubHoldAndEarnPage"));
const HubCasinoPage           = lazy(() => import("@/pages/HubCasinoPage"));
const WatchPreviewPage        = lazy(() => import("@/pages/WatchPreviewPage"));
const CrossversePage          = lazy(() => import("@/pages/CrossversePage"));
const GoldenBallPage          = lazy(() => import("@/pages/GoldenBallPage"));
const AdminCrossverse         = lazy(() => import("@/pages/admin/AdminCrossverse"));
const AdminHub                = lazy(() => import("@/pages/admin/AdminHub"));
const AdminHubUser            = lazy(() => import("@/pages/admin/AdminHubUser"));
const AdminTransmissions      = lazy(() => import("@/pages/admin/AdminTransmissions"));
const AdminMarketplace        = lazy(() => import("@/pages/admin/AdminMarketplace"));
const AdminPredictionShell    = lazy(() => import("@/pages/admin/AdminPredictionShell"));
const AdminHeadquartersShell  = lazy(() => import("@/pages/admin/AdminHeadquartersShell"));
const AdminLeaderboard        = lazy(() => import("@/pages/admin/AdminLeaderboard"));
const AdminPerformanceV2      = lazy(() => import("@/pages/admin/AdminPerformanceV2"));
const AdminGames              = lazy(() => import("@/pages/admin/AdminGames"));
const AdminSweepContest       = lazy(() => import("@/pages/admin/AdminSweepContest"));
const AdminTierPerks          = lazy(() => import("@/pages/admin/AdminTierPerks"));
const AdminHubZodiac          = lazy(() => import("@/pages/admin/AdminHubZodiac"));
const AdminHubHoldAndEarn     = lazy(() => import("@/pages/admin/AdminHubHoldAndEarn"));
const AdminHubCasino          = lazy(() => import("@/pages/admin/AdminHubCasino"));
const AdminGoldenVault        = lazy(() => import("@/pages/admin/AdminGoldenVault"));
const AdminFamilyDemand       = lazy(() => import("@/pages/admin/AdminFamilyDemand"));
const AdminOrbis              = lazy(() => import("@/pages/admin/AdminOrbis"));
const HubOrbisPage            = lazy(() => import("@/pages/HubOrbisPage"));
const HubSweepPage            = lazy(() => import("@/pages/HubSweepPage"));
const PredictionAdmin         = lazy(() => import("@/pages/admin/PredictionAdmin"));

// Hub route-chunk fallback: covers the ~50-500 ms window between
// clicking a Hub nav item and the module's JS chunk landing. Uses the
// same `HubBootLoader` component (compact variant) so the interstitial
// feels intentional and Prophets-branded instead of a frozen previous
// page. Once the chunk lands, the destination page's own skeleton
// state takes over. Kept lightweight — pure CSS, no images.
import HubBootLoader from "@/components/hub/HubBootLoader";
function RouteFallback() {
  return <HubBootLoader label="Loading module…" />;
}


function App() {
  return (
    <AuthProvider>
      <LandingSettingsProvider>
        <BrowserRouter>
          <ScrollToTop />
          <Suspense fallback={<RouteFallback />}>
          <Routes>
            {/* Prophets ecosystem homepage */}
            <Route path="/" element={<ProphetsHome />} />

            {/* Public race + Oracle pages */}
            {/* Family Race has moved into the Hub. The old public
                route now redirects to the canonical /hub/family-race
                route so bookmarks and inbound links keep working; the
                redirected destination inherits the Hub's Discord
                holder-gating (no bypass). */}
            <Route path="/family-race" element={<Navigate to="/hub/family-race" replace />} />
            <Route path="/theoracle" element={<TheOracle />} />
            <Route path="/raffle" element={<RafflePage />} />
            {/* Clean public share URL `/raffle/:raffleId` — production
                serves this from the backend with personalized OG
                metadata for Discord/X crawlers. When a browser lands
                here directly (or the OG redirect races) the SPA
                canonicalizes to `/hub/marketplace/:raffleId` so the
                existing raffle popup flow runs unchanged. Fixed
                `/raffle` route above still wins by static-segment
                priority. */}
            <Route path="/raffle/:raffleId" element={<RaffleRedirect />} />
            <Route path="/wl" element={<WhitelistPage />} />
            <Route path="/wl/:slug" element={<WhitelistCampaignPage />} />

            {/* Standalone eligibility checker — reachable ONLY via a direct
                URL (deliberately absent from the main navigation menu). */}
            <Route path="/eligibility" element={<EligibilityPage />} />

            {/* Prophets Hub (V1) — public feed + Discord auth */}
            {/* Slice S8 (preview only) — /hub is the community HOME
                page when the HUB_NEW_NAV flag is on. Legacy /hub
                behaviour (Transmissions feed) is still reachable at
                /hub/transmissions; the personal Dashboard moved into
                Profile → Dashboard and is also directly reachable at
                /hub/dashboard. */}
            <Route path="/hub" element={
              HUB_FEATURE_FLAGS.HUB_NEW_NAV ? <HubHomePage /> : <HubPage />
            } />
            <Route path="/hub/dashboard" element={<HubDashboardPage />} />
            <Route path="/hub/transmissions" element={<HubTransmissionsPage />} />
            <Route path="/hub/earn" element={<HubEarnLandingPage />} />
            <Route path="/hub/fragments" element={<HubFragmentsPage />} />
            <Route path="/hub/profile" element={<HubProfilePage />} />
            {/* PROPHETS FRIEND invitation landing (Phase 1). Preserves
                the code through Discord OAuth and auto-redeems on the
                callback for non-holders. */}
            <Route path="/hub/invite/:code" element={<HubInvitePage />} />
            {/* Clean public share URL `/invite/:code` — production
                serves this from the backend with personalized OG
                metadata for Discord/X crawlers. When a browser
                lands here directly (or the OG redirect races) the
                SPA canonicalizes to `/hub/invite/:code` so the
                existing landing/onboarding flow runs unchanged and
                the internal `/api/hub/invites/og/...` surface is
                never used as the user-facing URL. */}
            <Route path="/invite/:code" element={<InviteRedirect />} />
            {/* Slice S11 — MY PORTFOLIO promoted to a top-level Hub nav
                entry. The old sub-tab URL `/hub/profile?tab=portfolio`
                redirects to this canonical route from inside
                HubProfilePage so bookmarks / admin preview links
                keep working. */}
            <Route path="/hub/portfolio" element={<HubPortfolioPage />} />
            <Route path="/hub/quiz" element={
              HUB_FEATURE_FLAGS.HUB_QUIZ_ENABLED
                ? <HubQuizPage />
                : <Navigate to="/hub" replace />
            } />
            <Route path="/hub/daily-spin" element={<HubDailySpinPage />} />
            {/* Prophecy is guarded by a reversible feature flag. When
                disabled, the route safely redirects to /hub rather than
                rendering a partially-disabled interface. The page
                component, backend routes, DB records, and admin config
                are all preserved — flip the flag back to true to
                restore in one line. */}
            <Route path="/hub/prophecy" element={
              HUB_FEATURE_FLAGS.HUB_PROPHECY_ENABLED
                ? <HubProphecyPage />
                : <Navigate to="/hub" replace />
            } />
            <Route path="/hub/marketplace" element={<HubMarketplacePage />} />
            {/* Deep-link to a specific raffle — HubMarketplacePage
                auto-opens the raffle modal for {raffleId} when this
                route is hit. Closing the modal pops back to
                `/hub/marketplace`; browser Back does the same. */}
            {/* Pass B — the canonical public deep-link. Existing
                fixed Marketplace child routes (currently only
                `/raffle/:id` below) win by react-router's static-
                segment priority, so this dynamic `:raffleId` never
                shadows them. */}
            <Route path="/hub/marketplace/:raffleId"
                   element={<HubMarketplacePage />} />
            {/* Legacy `/hub/marketplace/raffle/:raffleId` deep-link.
                The SPA still opens the exact same raffle so old
                shared links keep working. New links always use the
                clean `/hub/marketplace/:raffleId` shape above. */}
            <Route path="/hub/marketplace/raffle/:raffleId"
                   element={<HubMarketplacePage />} />
            {/* ORBIS marketplace — isolated module, own route, own bundle.
                Loaded only when the user navigates here. /hub does NOT
                fetch or execute this chunk. */}
            <Route path="/hub/orbis" element={<HubOrbisPage />} />
            {/* Predictions canonical route + legacy alias — both mount the
                same component so admin/backend/user pipeline is unchanged. */}
            <Route path="/hub/predictions" element={<HubMyPredictionsPage />} />
            <Route path="/hub/my-predictions" element={<HubMyPredictionsPage />} />
            <Route path="/hub/predictor/:publicProfileId" element={<HubPredictorProfilePage />} />
            <Route path="/hub/leaderboard" element={<HubLeaderboardPage />} />
            <Route path="/hub/games" element={<HubGamesPage />} />
            <Route path="/hub/family-race" element={<HubFamilyRacePage />} />
            <Route path="/hub/zodiac" element={<HubZodiacPage />} />
            <Route path="/hub/sweep" element={<HubSweepPage />} />
            <Route path="/hub/golden-vault" element={<HubGoldenVaultPage />} />
            <Route path="/hub/golden-vault/chamber" element={<HubGoldenVaultPage />} />
            <Route path="/hub/hold-and-earn" element={<HubHoldAndEarnPage />} />
            {/* CASINO is guarded by a reversible feature flag. While
                disabled, the route (and any nested path under it)
                redirects to /hub with history replacement so direct URL
                entry, refreshes and old links never render Casino even
                for a frame. The page component and its chunk are only
                imported when the flag is on. */}
            <Route path="/hub/casino" element={
              HUB_FEATURE_FLAGS.HUB_CASINO_ENABLED
                ? <HubCasinoPage />
                : <Navigate to="/hub" replace />
            } />
            <Route path="/hub/casino/*" element={
              HUB_FEATURE_FLAGS.HUB_CASINO_ENABLED
                ? <HubCasinoPage />
                : <Navigate to="/hub" replace />
            } />
            <Route path="/hub/headquarters" element={<HubHeadquartersPage />} />
            <Route path="/hub/headquarters/:familyKey" element={<HubHeadquartersLandingPage />} />
            {/* Dev-only visual harness for the Watch card. Never
                reaches the API for state — safe to leave enabled. */}
            <Route path="/watch-preview" element={<WatchPreviewPage />} />

            {/* CROSSVERSE — public standalone page. Direct URL only in
                Phase 1 — deliberately NOT wired into the landing-page
                nav so THE ORACLE and every other landing element stay
                untouched. */}
            <Route path="/crossverse" element={<CrossversePage />} />

            {/* GOLDEN BALL GAME — public standalone scene (stage 1).
                Direct URL only: intentionally not wired into any nav. */}
            <Route path="/golden-ball" element={<GoldenBallPage />} />

            {/* Centralized admin */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={<AdminHome />} />
              <Route path="landing-page" element={<LandingPageAdmin />} />
              <Route path="family-race" element={<AdminDashboard />} />
              <Route path="the-oracle" element={<OracleAdmin />} />
              <Route path="raffle" element={<AdminRaffle />} />
              <Route path="raffle/:eventId" element={<AdminRaffle />} />
              <Route path="wl" element={<AdminWhitelist />} />
              <Route path="wl/global-rewards" element={<AdminGlobalRewards />} />
              <Route path="wl/:id" element={<AdminWhitelistCampaign />} />
              <Route path="hub" element={<AdminHub />} />
              <Route path="hub/users/:userId" element={<AdminHubUser />} />
              {/* Slice S10 — new top-level admin routes. Rendered
                  independently of ADMIN_NEW_IA so bookmarks still work
                  when the flag is off (sidebar hides the entries but
                  the routes remain reachable). */}
              <Route path="transmissions" element={<AdminTransmissions />} />
              <Route path="marketplace" element={<AdminMarketplace />} />
              {/* Slice S10b — Prediction shell (parent + Polymarket/NFT
                  sub-tabs) when the admin IA flag is on; else the legacy
                  flat PredictionAdmin. */}
              <Route path="prediction" element={
                HUB_FEATURE_FLAGS.ADMIN_NEW_IA ? <AdminPredictionShell /> : <PredictionAdmin />
              } />
              <Route path="leaderboard" element={<AdminLeaderboard />} />
              <Route path="performance-v2" element={<AdminPerformanceV2 />} />
              <Route path="games" element={<AdminGames />} />
              <Route path="sweep-contest" element={<AdminSweepContest />} />
              <Route path="tier-perks" element={<AdminTierPerks />} />
              <Route path="zodiac" element={<AdminHubZodiac />} />
              <Route path="hold-and-earn" element={<AdminHubHoldAndEarn />} />
              <Route path="casino" element={<AdminHubCasino />} />
              <Route path="golden-vault" element={<AdminGoldenVault />} />
              <Route path="family-demand" element={<AdminFamilyDemand />} />
              <Route path="orbis" element={<AdminOrbis />} />
              <Route path="headquarters" element={<AdminHeadquartersShell />} />
              <Route path="crossverse" element={<AdminCrossverse />} />
            </Route>

            {/* Legacy admin URLs → redirect to the centralized paths */}
            <Route path="/family-race/admin"       element={<Navigate to="/admin/family-race" replace />} />
            <Route path="/family-race/admin/login" element={<Navigate to="/admin/login" replace />} />
            <Route path="/theoracle/admin"         element={<Navigate to="/admin/the-oracle" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
          {/* Persistent SolRadio player — mounted ONCE, above the
              `<Routes>` switch, so its iframe DOM node survives every
              route change. See PersistentSolRadio.jsx for the full
              architectural rationale. */}
          <PersistentSolRadio />
        </BrowserRouter>
      </LandingSettingsProvider>
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0B0C10",
            border: "1px solid rgba(232,121,249,0.35)",
            color: "#F3F4F6",
            fontFamily: "IBM Plex Mono, monospace",
          },
        }}
      />
    </AuthProvider>
  );
}

export default App;

// Client-side fallback for the clean public share URL `/invite/:code`.
// Production routes this path to the backend which returns
// personalized OG/Twitter meta for social crawlers and then redirects
// human browsers to `/hub/invite/:code`. When the SPA receives the
// request directly (e.g. hot-reload preview, or the meta refresh
// races the JS), we canonicalize to the same landing page while
// preserving the code param — no invitation is ever redeemed or
// consumed by the redirect itself.
function InviteRedirect() {
  const params = window.location.pathname.match(/^\/invite\/([^/?#]+)/);
  const code = params ? decodeURIComponent(params[1]) : "";
  const target = code ? `/hub/invite/${code}` : "/hub";
  return <Navigate to={target} replace />;
}

// Client-side fallback for the clean public share URL `/raffle/:id`.
// Production routes this path to the backend which returns
// personalized OG/Twitter meta for social crawlers and then redirects
// human browsers to `/hub/marketplace/:id`. When the SPA receives the
// request directly (e.g. hot-reload preview, or the meta refresh
// races the JS), we canonicalize to the same landing page while
// preserving the id param — no ticket is ever purchased or FRG is
// debited by the redirect itself.
function RaffleRedirect() {
  const params = window.location.pathname.match(/^\/raffle\/([^/?#]+)/);
  const id = params ? decodeURIComponent(params[1]) : "";
  const target = id ? `/hub/marketplace/${id}` : "/hub/marketplace";
  return <Navigate to={target} replace />;
}
